import { useAuthStore } from '@/stores/useAuthStore'
import DashboardView from '@/views/DashboardView.vue'
import {
  createRouter,
  type RouteLocationNormalized,
  type NavigationGuardNext,
  createWebHistory,
} from 'vue-router'

// ─── Role Constants ────────────────────────────────────────────────────────────
export const ROLES = {
  ADMIN: 'admin',
  CASHIER: 'cashier',
} as const

export type Role = (typeof ROLES)[keyof typeof ROLES]

const POS = () => import('@/views/POS.vue')

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/login',
      name: 'login',
      component: () => import('@/views/Auth/LoginView.vue'),
      meta: {
        title: 'Login',
        requiresAuth: false,
        hideLayout: true,
      },
    },
    {
      path: '/reset-password',
      name: 'ResetPassword',
      component: () => import('@/views/Auth/ResetPassword.vue'),
      props: (route) => ({ token: route.query.token }),
      meta: {
        title: 'Reset Password',
        requiresAuth: false,
        hideLayout: true,
      },
    },
    {
      path: '/unauthorized',
      name: 'Unauthorized',
      component: () => import('@/views/UnauthorizedView.vue'),
      meta: {
        title: 'Unauthorized',
        requiresAuth: false,
        hideLayout: true,
      },
    },

    // ─── Shared Routes (Admin + Cashier) ────────────────────────────────────
    {
      path: '/',
      name: 'dashboard',
      component: DashboardView,
      meta: {
        title: 'Dashboard',
        requiresAuth: true,
        roles: [ROLES.ADMIN, ROLES.CASHIER],
      },
    },
    {
      path: '/point-of-sale',
      name: 'POS',
      component: POS,
      meta: {
        title: 'Point Of Sale',
        requiresAuth: true,
        roles: [ROLES.ADMIN, ROLES.CASHIER],
      },
    },
    {
      path: '/sales',
      name: 'sales',
      component: () => import('@/views/SalesView.vue'),
      meta: {
        title: 'Sales Management',
        requiresAuth: true,
        roles: [ROLES.ADMIN, ROLES.CASHIER],
        // Cashier will see filtered results (handled in the component via authStore.user)
      },
    },
    {
      path: '/eis/inventory-sync',
      name: 'eis-inventory-sync',
      component: () => import('@/views/EIS/InventorySync.vue'),
      meta: {
        title: 'EIS Inventory Sync',
        requiresAuth: true,
        roles: [ROLES.ADMIN, ROLES.CASHIER],
      },
    },

    // ─── Admin-Only Routes ──────────────────────────────────────────────────
    {
      path: '/invoices',
      name: 'Invoices',
      redirect: '/invoices/list',
      meta: {
        requiresAuth: true,
        title: 'Invoices',
        roles: [ROLES.ADMIN],
      },
      children: [
        {
          path: 'list',
          name: 'InvoiceList',
          component: () => import('@/views/invoices/InvoiceList.vue'),
          meta: {
            requiresAuth: true,
            title: 'Invoice List',
            roles: [ROLES.ADMIN],
            breadcrumb: [
              { text: 'Home', to: '/' },
              { text: 'Invoices', to: '/invoices' },
            ],
          },
        },

        {
          path: '/stock-counting/:id',
          name: 'StockCounting',
          component: () => import('@/views/StockCountingView.vue'),
          meta: {
            requiresAuth: true,
            roles: [ROLES.ADMIN],
          },
        },
        {
          path: ':id',
          name: 'InvoiceDetail',
          component: () => import('@/views/invoices/InvoiceDetail.vue'),
          meta: {
            requiresAuth: true,
            title: 'Invoice Detail',
            roles: [ROLES.ADMIN],
            breadcrumb: [
              { text: 'Home', to: '/' },
              { text: 'Invoices', to: '/invoices' },
              { text: 'Detail', to: '' },
            ],
          },
        },

        {
          path: ':id/send',
          name: 'InvoiceSendEmail',
          component: () => import('@/views/invoices/InvoiceSendEmail.vue'),
          meta: {
            requiresAuth: true,
            title: 'Send Invoice',
            roles: [ROLES.ADMIN],
            breadcrumb: [
              { text: 'Home', to: '/' },
              { text: 'Invoices', to: '/invoices' },
              { text: 'Send', to: '' },
            ],
          },
        },
      ],
    },
    {
      path: '/eis/terminal-management',
      name: 'eis-terminal-management',
      component: () => import('@/views/EIS/TerminalManagement.vue'),
      meta: {
        requiresAuth: true,
        title: 'Terminal Management',
        roles: [ROLES.ADMIN],
      },
    },
    {
      path: '/inventory/stock-reconciliation',
      name: 'stock-reconciliation',
      component: () => import('@/views/ReconciliationView.vue'),
      meta: {
        title: 'Stock Reconciliation',
        requiresAuth: true,
        roles: [ROLES.ADMIN],
      },
    },
    {
      path: '/reconciliation/:id',
      name: 'ViewReconciliation',
      component: () => import('@/views/ViewReconciliation.vue'),
      meta: {
        requiresAuth: true,
        roles: [ROLES.ADMIN],
      },
    },
    {
      path: '/reconciliation/:id/adjustments',
      name: 'ViewReconciliationAdjustments',
      component: () => import('@/views/Viewreconciliationadjustments.vue'),
      meta: {
        requiresAuth: true,
        roles: [ROLES.ADMIN],
      },
    },
    {
      path: '/adjustments/history',
      name: 'AdjustmentHistory',
      component: () => import('@/views/Adjustmenthistory.vue'),
      meta: {
        requiresAuth: true,
        roles: [ROLES.ADMIN],
      },
    },
    {
      path: '/reconciliation/:id/adjust',
      name: 'StockAdjustment',
      component: () => import('@/views/Stockadjustment.vue'),
      meta: {
        title: 'Stock Adjustment',
        requiresAuth: true,
        roles: [ROLES.ADMIN],
      },
    },
    {
      path: '/inventories',
      name: 'eis-stock',
      component: () => import('@/views/InventoryView.vue'),
      meta: {
        title: 'EIS Stock',
        requiresAuth: true,
        roles: [ROLES.ADMIN],
      },
    },
    {
      path: '/customers',
      name: 'customers',
      component: () => import('@/views/CustomersView.vue'),
      meta: {
        title: 'Customer Management',
        requiresAuth: true,
        roles: [ROLES.ADMIN],
      },
    },
    {
      path: '/vendors',
      name: 'vendors',
      component: () => import('@/views/VendorsView.vue'),
      meta: {
        title: 'Vendor Management',
        requiresAuth: true,
        roles: [ROLES.ADMIN],
      },
    },
    {
      path: '/users',
      name: 'users',
      component: () => import('@/views/UsersView.vue'),
      meta: {
        title: 'User Management',
        requiresAuth: true,
        roles: [ROLES.ADMIN],
      },
    },
    {
      path: '/company',
      name: 'company',
      component: () => import('@/views/CompanyView.vue'),
      meta: {
        title: 'Company Profile',
        requiresAuth: true,
        roles: [ROLES.ADMIN],
      },
    },
    {
      path: '/company-configurations',
      name: 'company-configurations',
      component: () => import('@/views/Configurations.vue'),
      meta: {
        title: 'Configurations Management',
        requiresAuth: true,
        roles: [ROLES.ADMIN],
      },
    },
    {
      path: '/company-activation',
      name: 'CompanyActivation',
      component: () => import('@/views/CompanyActivation.vue'),
      meta: {
        requiresAuth: false,
        title: 'Company Activation',
      },
    },

    // ─── Fallback ───────────────────────────────────────────────────────────
    {
      path: '/:pathMatch(.*)*',
      name: 'not-found',
      redirect: '/',
    },
  ],
})

// ─── Global Navigation Guard ──────────────────────────────────────────────────
router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore()

  if (!authStore._hydrated) {
    await new Promise((resolve) => setTimeout(resolve, 0))
    authStore._markHydrated()
  }

  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth !== false)
  const isPublicRoute =
    to.name === 'login' ||
    to.name === 'ResetPassword' ||
    to.name === 'AdminSubscriptionKeys' ||
    to.name === 'Unauthorized'

  if (requiresAuth && !authStore.isAuthenticated) {
    if (!isPublicRoute) {
      next({ name: 'login', query: { redirect: to.fullPath }, replace: true })
      return
    }
  }

  if (to.name === 'login' && authStore.isAuthenticated) {
    const redirectPath = (to.query.redirect as string) || '/'
    next(redirectPath)
    return
  }

  if (authStore.isAuthenticated && to.meta.roles) {
    const allowedRoles = to.meta.roles as Role[]
    const userRole = authStore.user?.role?.toLowerCase() as Role

    if (!allowedRoles.includes(userRole)) {
      next({ name: 'Unauthorized' })
      return
    }
  }

  if (to.meta.title) {
    document.title = `${to.meta.title} - iT Centre EIS Tax System`
  }

  next()
})

router.onError(() => {})

export default router
